import { defineStore } from 'pinia'
import CognitoAuth from '@/app/core/services/auth/cognito'
import { menu } from '@/config'
import { usePayrollStore } from './payroll-data'
import { getPayroll } from '@/app/core/services/payrolls'
import { getRolesAssigned } from '@/app/core/services/roles'

const cognitoAuth = CognitoAuth.getInstance()

export const useUserStore = defineStore('user', {
  persist: true,
  state: () => {
    return {
      user: null as UserInfo | null,
      permissions: [] as string[],
      emailForgot: '' as string,
      pass: '' as string,
      notification_trafficLight: '' as any,
      tokenInvalid: false as boolean,
      prefixes_formulas: null as typesFormula | null
    }
  },
  getters: {
    async isAuthenticated(): Promise<boolean> {
      return Promise.resolve(!!this.user?.token)
    },
    sidebarMenu(): MenuItem[] {
      const newMenu: MenuItem[] = []
      const allMenu = JSON.parse(JSON.stringify(menu))
      allMenu.forEach((menuParent: any) => {
        if (
          menuParent.children === undefined &&
          (menuParent.permission === undefined ||
            this.permissions.includes(menuParent.permission))
        ) {
          newMenu.push(menuParent)
          return
        }
        const subMenuChildren = menuParent.children?.map(
          (menuChildren: any) => {
            if (
              (menuChildren.children?.length === 0 ||
                menuChildren.children === undefined) &&
              (menuChildren.permission === undefined ||
                this.permissions.includes(menuChildren.permission))
            ) {
              return menuChildren
            }
            const subMenuGrandson =
              menuChildren.children?.filter((menuGrandson: any) =>
                this.permissions.includes(menuGrandson.permission)
              ) ?? []
            menuChildren.children = subMenuGrandson

            if (menuChildren.children.length > 0) {
              return menuChildren
            }
            return null
          }
        )
        if (subMenuChildren && Array.isArray(subMenuChildren)) {
          menuParent.children = subMenuChildren.filter((i: any) => i != null)
        } else {
          menuParent.children = []
        }

        if (menuParent.children?.length > 0) {
          newMenu.push(menuParent)
        }
      })
      return newMenu
    },
    hasPermission() {
      return (permission: string) =>
        !permission || this.permissions.includes(permission)
    }
  },
  actions: {
    async login(dataUser: any): Promise<boolean | Error> {
      try {
        await cognitoAuth.login(dataUser)
        const cognitoUser = cognitoAuth.getCurrentUser()
        const storePayroll = usePayrollStore()
        if (cognitoUser) {
          const token = cognitoAuth.getIdToken()
          const claims = await cognitoAuth.getApsClaims(token.token)
          const attrs: any = await cognitoAuth.getAttributes(cognitoUser)
          this.user = {
            id: cognitoUser.getUsername(),
            name: claims.xUserName,
            email: claims.xUserEmail,
            workspaceId: claims.xWorkspaceId,
            roleIds: claims.xAllowedRoles,
            payrolls: attrs?.allowed_payrolls?.split(','),
            token: token
          }
          // get Roles
          this.user.roles = await getRolesAssigned(claims.xAllowedRoles)
          // get actual role
          this.user.actualRole = this.user?.roles?.find(
            role => role.id === claims.xDefaultRole
          )
          this.permissions = this.user.actualRole?.permissions ?? []
          // get payrolls
          const payrollData = await getPayroll(this.user?.id)
          storePayroll.setAllowedPayroll(payrollData)
          // go to home page
          return true
        }
        return false
      } catch (error) {
        return error as Error
      }
    },
    async passwordTemporary(dataUser: any): Promise<boolean | Error> {
      try {
        await cognitoAuth.login(dataUser)
        const cognitoUser = cognitoAuth.getCurrentUser()
        if (cognitoUser) {
          return true
        }
        return false
      } catch (error) {
        return error as Error
      }
    },
    async passwordReset(username: string): Promise<boolean | string> {
      return await cognitoAuth.passwordReset(username)
    },
    async passwordResetConfirm(
      username: string,
      code: string,
      newPassword: string
    ): Promise<boolean> {
      return await cognitoAuth.confirmPasswordReset(username, code, newPassword)
    },
    logout() {
      cognitoAuth.logout()
      this.user = null
    },
    async changeNameAndPassword(
      newName: string,
      oldPass: string,
      newPass: string
    ): Promise<boolean> {
      const changed = await cognitoAuth?.changeNameAndPassword(
        newName,
        oldPass,
        newPass
      )
      if (!changed) {
        return false
      }
      if (!this.user) return false

      this.user.name = newName

      return true
    },
    async getValidToken(): Promise<string> {
      try {
        if (!this.user) {
          throw new Error('User not logged in')
        }
        const currentToken = this.user.token
        // Verificar si el token actual ha expirado
        if (
          currentToken &&
          new Date(Date.now()) < new Date(currentToken.expiresAt * 1000)
        ) {
          // El token actual aún es válido, retornar el token actual
          return currentToken.token
        } else {
          // El token ha expirado o no está presente, refrescarlo
          const refreshedSession = await cognitoAuth.refreshToken()
          if (!refreshedSession) {
            throw new Error('Error refreshing token')
          }
          const newIdToken = refreshedSession.getIdToken().getJwtToken()
          // Actualizar el token
          this.user.token = {
            token: newIdToken,
            expiresAt: refreshedSession.getIdToken().getExpiration()
          }
          return newIdToken
        }
      } catch (error) {
        throw new Error('Error al obtener el token válido')
      }
    }
  }
})

interface UserInfo {
  id: string
  name: string
  email: string
  workspaceId: string
  roleIds: string[]
  payrolls: string[]
  token: {
    token: string
    expiresAt: number
  }
  actualRole?: Role
  roles?: Role[]
}

interface Role {
  id: string
  name: string
  code: string
  permissions: string[]
}

interface MenuItem {
  name: string
  icon?: string
  route?: string
  idMenu?: string
  children?: MenuItem[]
  showItems?: boolean
  permission?: string
}

interface typesFormula {
  personalized: any[]
  standard: any[]
  entity: any[]
  saved: any[]
}
